<script>
import axios from 'axios'
import moment from 'moment'
import layout from '@src/mixins/layout.vue'
import NavigateMixin from '@src/mixins/navigate-mixin.vue'
import { providerService } from '@/src/services/safehaven/ProviderService'
import ProviderDetails from '@views/safehaven/health-help/telehealth/provider-details.vue'

export default {
  components: { ProviderDetails },
  page: {
    title: 'Confirm appointment',
    meta: [{ name: 'description', content: 'The Confirm Appointment page.' }],
  },
  mixins: [layout, NavigateMixin],
  data() {
    return {
      appointmentProviderDetails: {},
      providerDetails: {},
      providerName: '',
      appointmentDate: '',
      formattedAppointmentDate: '',
      appointmentTime: '',
      appointmentNotes: '',
      cancelApp: false,
      showProviderDetails: false,
      icsFileBlob: null,
    }
  },
  mounted: function() {
    const eventId = this.$route.params.eventId
    this.getEventDetails(eventId)
  },

  methods: {
    changeEvent(ev) {
      this.getEventDetails(parseInt(ev.target.value))
    },
    getEventDetails(eventId) {
      this.loading = true
      axios
        .get('/api/v1/me/')
        .then((response) => {
          const appointmentList = response.data.patient_appointment_details
          this.appointmentProviderDetails = appointmentList.find(
            (appointment) => appointment.availability_det_id === eventId
          )
          // TODO: Uncomment below lines when credentials appear in response
          if (this.appointmentProviderDetails.provider_credentials) {
            this.appointmentProviderDetails.credentials = this.getCredentials(
              this.appointmentProviderDetails.provider_credentials
            )
          } else {
            this.appointmentProviderDetails.credentials = ''
          }
          this.appointmentDate = this.appointmentProviderDetails.appointment_date
          this.formattedAppointmentDate = moment(
            this.appointmentProviderDetails.appointment_date
          ).format('dddd, MMM D')

          this.appointmentTime = moment(
            this.appointmentProviderDetails.appointment_time,
            ['h:mm A']
          ).format('hh:mm a')
        })
        .finally(() => {
          this.getUpdatedProviderDetails()

          this.downloadICSFile()
        })
    },
    cancelEvent() {
      axios
        .post('/api/v1/provider/cancel_appointment/', {
          provider_id: this.appointmentProviderDetails.provider_id,
          availability_det_id: this.appointmentProviderDetails
            .availability_det_id,
          cancellation_notes: 'Due to some urgent call, I need to cancel',
          appointment_date: this.appointmentProviderDetails.appointment_date,
          is_cancel_from_provider: false,
        })
        .then((response) => {
          document.getElementById('updateCalnederNew').click()
          this.navigateToScreen('cancel-confirmation')
          this.toggleCancelOptions()
        })
        .finally(() => {})
    },
    navigateToCalendar() {
      // Todo: Uncomment below code when Calendar page is available
      this.navigateToScreen('safehaven-calendar')
    },
    toggleCancelOptions() {
      this.cancelApp = !this.cancelApp
    },
    getCredentials(credArr) {
      let credentials = ''
      credArr.map((cred) => {
        credentials = credentials + ', ' + cred.credential_name
      })
      return credentials
    },
    changeMeetingTime() {
      this.showProviderDetails = !this.showProviderDetails
      this.getUpdatedProviderDetails()
    },
    dismissDialog() {
      this.showProviderDetails = false
    },
    getUpdatedProviderDetails() {
      this.loading = true
      const providerDetailReqObj = {
        provider_type: this.appointmentProviderDetails.provider_speciality,
        date: this.appointmentDate,
        provider_id: this.appointmentProviderDetails.provider_id,
      }
      providerService.getProviderDetails(providerDetailReqObj).then((res) => {
        const response = this.parseResponse(res.data)
        this.providerDetails = response[0]
        this.loading = false
        document.getElementById('updateCalnederNew').click()
      })
    },
    parseResponse(resData) {
      resData.map((provider) => {
        provider.credentials = this.getCredentials(provider.credentials)
      })
      return resData
    },
    downloadICSFile() {
      const reqObj = {
        appointment_date: this.appointmentProviderDetails.appointment_date,
        appointment_time: moment(
          this.appointmentProviderDetails.appointment_time,
          ['h:mm A']
        ).format('hh:mm A'),
        doxy_me_link: this.appointmentProviderDetails.video_conference_link,
      }
      axios
        .post('/api/v1/patients/get_download_ics_file/', reqObj)
        .then((response) => {
          this.createFile(response.data)
        })
        .catch((error) => {
          console.warn('error', JSON.stringify(error))
        })
        .finally(() => {})
    },
    createFile(data) {
      this.icsFileBlob = new Blob([data], { type: 'text/calendar' })
      if (this.icsFileBlob !== null) {
        window.URL.revokeObjectURL(this.icsFileBlob)
      }
    },
    async downloadFile() {
      if (!this.icsFileBlob) {
        return
      }

      const pickerOptions = {
        suggestedName: 'Telehealth-meeting.ics',
        types: [
          {
            description: 'Meeting Invite',
            accept: {
              'text/plain': ['.ics'],
            },
          },
        ],
      }

      const fileHandle = await window.showSaveFilePicker(pickerOptions)

      const writableFileStream = await fileHandle.createWritable()

      await writableFileStream.write(this.icsFileBlob)

      await writableFileStream.close()
    },
  },
}
</script>

<template>
  <v-container class="$style.mainContainer">
    <ProviderDetails
      :show-details="showProviderDetails"
      :provider-details="providerDetails"
      :selected-date="appointmentDate || ''"
      :is-from-reschedule="true"
      :appointment-details="appointmentProviderDetails"
      @close-dialog="dismissDialog"
    />
    <v-row>
      <v-col
        cols="24"
        sm="12"
        md="12"
        lg="5"
        xl="4"
        :align="notDesktop ? 'center' : 'start'"
        fill-height
      >
        <div :class="$style.textContainer">
          <a :class="$style.titleText" @click="navigateToCalendar"
            >View Calendar
          </a>
        </div>

        <div :class="$style.textContainer">
          <p :class="$style.titleText">Event</p>
          <p :class="$style.appointmentText">Telehealth Meeting</p>
        </div>

        <div :class="$style.textContainer">
          <p :class="$style.titleText">Date & Time</p>
          <p :class="$style.appointmentText">
            {{ formattedAppointmentDate }} at {{ appointmentTime }}
          </p>
          <a :class="$style.ancolButton" @click="downloadFile">
            Export .ics file to your calendar
          </a>
        </div>
        <div :class="$style.textContainer">
          <p :class="$style.titleText">Provider</p>
          <p :class="$style.appointmentText">
            {{ appointmentProviderDetails.provider_name }}
            {{ appointmentProviderDetails.credentials }}
          </p>
        </div>
        <div :class="$style.textContainer">
          <p :class="$style.additionalText">
            <span :class="$style.titleText">Additional Notes </span>
            <span>(optional)</span>
          </p>
          <p :class="$style.additionalNotesText">
            {{ appointmentProviderDetails.additional_notes }}
          </p>
        </div>

        <div :class="$style.textContainer">
          <p :class="$style.additionalText">
            <span :class="$style.titleText">Waiting Room</span>
          </p>
          <a
            :class="$style.doxyLinkText"
            :href="appointmentProviderDetails.video_conference_link"
            target="_blank"
          >
            {{ appointmentProviderDetails.video_conference_link }}
          </a>
          <p :class="$style.additionalNotesText">
            At the scheduled time, click on the link above to open a new browser
            tab to enter your provider’s waiting room. Follow the instructions
            and wait for your provider to start the telehealth meeting.
          </p>

          <div class="d-flex flex-row mt-2" :class="$style.cardCustom">
            <div v-if="!cancelApp">
              <a :class="$style.ancolButton" @click="changeMeetingTime"
                >Reschedule</a
              >
              <span :class="$style.spanSeperator">&nbsp;| &nbsp;</span>
              <a :class="$style.ancolButton" @click="toggleCancelOptions"
                >Cancel</a
              >
            </div>
            <div v-else>
              <p :class="$style.titleText">Are you sure?</p>
              <a :class="$style.ancolButton" @click="toggleCancelOptions"
                >No don't Cancel</a
              >
              <span :class="$style.spanSeperator">&nbsp;| &nbsp;</span>
              <a :class="$style.ancolButtonBold" @click="cancelEvent"
                >Yes Cancel it</a
              >
            </div>
          </div>
        </div>
      </v-col>
    </v-row>
    <button id="changeEventButton" hidden @click="changeEvent($event)"></button>
  </v-container>
</template>

<style lang="scss" module>
@import '@design';

p {
  margin-bottom: 0 !important;
}

.mainContainer {
  padding-right: 20px !important;
  padding-left: 20px !important;
}

.textContainer {
  margin-top: 20px;
  text-align: left;
}

.serverErrorMsg {
  margin-top: 14px;
  margin-left: 92px;
  font-size: 12px;
  color: #ca024f;
}

.titleText {
  margin-bottom: 0 !important;
  font-size: 15px;
  font-weight: 700;
  line-height: 150%;
  color: #2c3a4b;
}

.appointmentText {
  margin-bottom: 0 !important;
  font-size: 18px;
  font-weight: 700;
  line-height: 30px;
  color: #09101d;
  letter-spacing: -0.3px;
}

.doxyLinkText {
  margin-top: 5px;
  margin-bottom: 0 !important;
  font-size: 18px;
  font-weight: 700;
  line-height: 30px;
  color: #0476d9;
  letter-spacing: -0.3px;
}

.additionalNotesText {
  margin-top: 5px;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  color: #000;
}

.vtextArea {
  margin-top: 15px !important;
}

.title {
  font-size: 28px;
  font-weight: 400;
}

.dialogMenu {
  border-radius: 9px !important;
  box-shadow: 0 0 16px rgba(0, 0, 0, 0.5);
}

.termsContainer {
  padding: 5px;
  margin-top: 30px !important;
}

.termsText {
  font-size: 12px;
  font-weight: 400;
  line-height: 18px;
  text-align: left;
  letter-spacing: 0;
}

.privacyText {
  font-size: 12px;
  font-weight: 700;
  line-height: 18px;
  color: #0476d9;
  text-align: left;
  text-decoration: none;
  letter-spacing: 0;
}

.cardCustom {
  padding: 10px 0 10px 0;
}

.ancolButton {
  padding-right: 10px;
  margin-top: 10px;
  font-size: 14px;
}

.ancolButtonBold {
  padding-right: 10px;
  font-size: 14px;
  font-weight: bold;
}

.spanSeperator {
  color: #ccc;
}

@media screen and (max-width: 960px) {
  .buttonContainer {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: space-evenly;
    margin-top: 10px;
  }

  .fullWidthBtn {
    width: 90%;
    height: 46px !important;
    font-size: 18px !important;
    text-transform: unset !important;
  }

  .cancelButton {
    margin-top: 10px;
  }

  .termsContainer {
    padding: 10px;
    margin-top: 20px !important;
  }
}
</style>

<style scoped>
.peerHeading {
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: 38px;
  text-align: left;
  letter-spacing: -0.3px;
}

.backButtonContainer {
  align-items: center;
  padding: 10px;
}
</style>
